/**
 * Web Push registration (skeleton).
 *
 * The Worker stores the subscription and can wake the phone with a payload-less
 * push; the service worker shows a generic notification because RFC 8291 payload
 * encryption is still pending (see apps/worker/README.md).
 */
import type { WorkerHealthDetails } from './api/worker';

export interface PushEnableResult {
  ok: boolean;
  message: string;
  subscriptionCount?: number;
}

export function isPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    typeof Notification !== 'undefined'
  );
}

export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const bytes = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return bytes;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('./sw.js', { scope: './' });
  } catch {
    return null;
  }
}

export function vapidKeyFromHealth(health: { details?: Record<string, unknown> }): string | null {
  const details = health.details as WorkerHealthDetails | undefined;
  const key = details?.web_push?.vapid_public_key;
  return typeof key === 'string' && key.length > 0 ? key : null;
}

export async function enablePushNotifications(options: {
  workerUrl: string;
  token: string;
  vapidPublicKey: string;
}): Promise<PushEnableResult> {
  if (!isPushSupported()) {
    return {
      ok: false,
      message:
        '当前浏览器/上下文不支持 Web Push（需要 HTTPS 或 localhost，iOS 需先“添加到主屏幕”）',
    };
  }
  if (!options.workerUrl) return { ok: false, message: '请先在设置中填写 Worker 地址' };
  if (!options.token) return { ok: false, message: '请先完成设备配对' };
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { ok: false, message: '通知权限被拒绝' };
    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) registration = (await registerServiceWorker()) ?? undefined;
    if (!registration) return { ok: false, message: 'Service Worker 注册失败' };
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(
        options.vapidPublicKey,
      ) as unknown as BufferSource,
    });
    const payload = subscription.toJSON();
    const response = await fetch(`${options.workerUrl.replace(/\/+$/u, '')}/v1/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${options.token}`,
      },
      body: JSON.stringify({
        endpoint: payload.endpoint,
        keys: {
          p256dh: payload.keys?.p256dh ?? '',
          auth: payload.keys?.auth ?? '',
        },
      }),
    });
    if (!response.ok) {
      return { ok: false, message: `订阅上报失败（HTTP ${response.status}）` };
    }
    const body = (await response.json()) as { subscription_count?: number };
    return {
      ok: true,
      message: '已注册推送通知（当前为无载荷骨架：通知内容为通用提示）',
      subscriptionCount: body.subscription_count,
    };
  } catch (error) {
    return {
      ok: false,
      message: error instanceof Error ? `推送注册失败：${error.message}` : '推送注册失败',
    };
  }
}

export async function disablePushNotifications(): Promise<void> {
  if (!isPushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    await subscription?.unsubscribe();
  } catch {
    /* ignore */
  }
}

/** Ask the Worker to send a real Web Push to this device (verifies server side). */
export async function sendPushTest(options: {
  workerUrl: string;
  token: string;
}): Promise<{ ok: boolean; message: string }> {
  if (!options.workerUrl) return { ok: false, message: '请先填写 Worker 地址' };
  if (!options.token) return { ok: false, message: '请先完成设备配对' };
  try {
    const response = await fetch(`${options.workerUrl.replace(/\/+$/u, '')}/v1/push/test`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${options.token}` },
    });
    const body = (await response.json()) as {
      ok?: boolean;
      sent?: number;
      failed?: number;
      subscription_count?: number;
      skipped_reason?: string;
      error?: { message?: string };
    };
    if (!response.ok || body.ok !== true) {
      return {
        ok: false,
        message: body.error?.message ?? `推送测试失败（HTTP ${response.status}）`,
      };
    }
    if ((body.sent ?? 0) > 0) {
      return { ok: true, message: `测试推送已发送（订阅 ${body.subscription_count ?? 0} 个）` };
    }
    return {
      ok: false,
      message: `未发送：subscription_count=${body.subscription_count ?? 0}，reason=${body.skipped_reason ?? 'unknown'}`,
    };
  } catch (error) {
    return {
      ok: false,
      message: error instanceof Error ? `推送测试失败：${error.message}` : '推送测试失败',
    };
  }
}
